export const LEGAL_ENTITY_TYPE_OPTIONS = [
    {
        value: "limited_liability_company",
        label: "Limited Liability Company (LLC)"
    },
    { value: "corporation", label: "Corporation" },
    { value: "partnership", label: "Partnership" },
    { value: "foundation", label: "Foundation" },
    { value: "trust", label: "Trust" },
    { value: "sole_trader", label: "Sole Proprietorship / Trader" },
    { value: "dao", label: "DAO / Unincorporated" }
] as const;

export type LegalEntityTypeOption = (typeof LEGAL_ENTITY_TYPE_OPTIONS)[number]["value"];
export type LegalEntityTypeSelection = LegalEntityTypeOption | "other";

const LEGAL_ENTITY_TYPE_LEGACY_LABELS: Partial<Record<LegalEntityTypeOption, readonly string[]>> = {
    limited_liability_company: ["Limited Liability Company"],
    sole_trader: ["Sole Trader"],
    dao: ["DAO"]
};

/**
 * Form-only representation of Entity Type. The API still receives one string:
 * either a canonical option label or the applicant's custom Other value.
 */
export interface LegalEntityTypeDraft {
    selection: LegalEntityTypeSelection | null;
    other: string;
}

export const EMPTY_LEGAL_ENTITY_TYPE_DRAFT: LegalEntityTypeDraft = {
    selection: null,
    other: ""
};

export function isLegalEntityTypeSelection(value: unknown): value is LegalEntityTypeSelection {
    return value === "other" || LEGAL_ENTITY_TYPE_OPTIONS.some((option) => option.value === value);
}

export function legalEntityTypeLabel(selection: LegalEntityTypeSelection | null): string | null {
    if (selection === "other") return "Other";
    return LEGAL_ENTITY_TYPE_OPTIONS.find((option) => option.value === selection)?.label ?? null;
}

/** Resolve the form-only choice to the string stored by yaas-api. */
export function legalEntityTypeValue(draft: LegalEntityTypeDraft): string {
    if (draft.selection === "other") return draft.other.trim();
    return legalEntityTypeLabel(draft.selection) ?? "";
}

/**
 * Rehydrate a saved API string. Recognized labels select their canonical row;
 * every historical/custom value remains editable through Other.
 */
export function legalEntityTypeDraftFromValue(value: string | null | undefined): LegalEntityTypeDraft {
    const trimmed = value?.trim() ?? "";
    if (!trimmed) return { ...EMPTY_LEGAL_ENTITY_TYPE_DRAFT };

    const normalized = trimmed.toLowerCase();
    const match = LEGAL_ENTITY_TYPE_OPTIONS.find((option) => {
        if (option.label.toLowerCase() === normalized) return true;
        return LEGAL_ENTITY_TYPE_LEGACY_LABELS[option.value]?.some((label) => label.toLowerCase() === normalized);
    });
    if (match) {
        return { selection: match.value, other: "" };
    }
    return { selection: "other", other: trimmed };
}
